import { businessDayOf, parisHM, todayIsoParis, type PunchLike } from './domain.js';
import { timeToMinutes } from './boardCoverage.js';

// One business day's punches, split into the three groups the operator reads
// them in (2026-08-06). Extracted from the Pointeuse kiosk when « Pointages »
// needed the same chart: the grouping is domain logic, the chart is rendering.
//
// The three groups are the same partition on every day; only what they MEAN
// shifts with the day's position relative to today, which is why `position`
// travels with the result and the labels are chosen by the caller:
//
//   openRows   — at least one punch without a clock-out.
//                today → « Présent·e·s » (on site right now)
//                past  → « Sans départ » (a payroll error to correct)
//   closedRows — punched, every interval closed.
//                today → « Parti·e·s »   past → « Présent·e·s » (they worked)
//   noPunchIds — on the roster, no punch at all.
//                today → « Pas de pointage »   past → « En repos »
//
// A future day has no punches by construction, so callers render nothing.

export type DayPosition = 'past' | 'today' | 'future';

/** One clock interval, positioned on the day's minute axis. */
export interface PresenceSegment {
  /** Minutes from the business day's 00:00. May exceed 1440 — see minutesInBusinessDay. */
  startMin: number;
  /** null while the punch is still open — the caller draws it to « now ». */
  endMin: number | null;
  /** 'HH:MM–HH:MM', or 'HH:MM–…' while open. */
  label: string;
}

export interface PresenceRow {
  employeeId: string;
  /** Chronological, earliest first. */
  segments: PresenceSegment[];
  /** True when any segment is still open. */
  open: boolean;
}

export interface PresenceDay {
  day: string;
  position: DayPosition;
  openRows: PresenceRow[];
  closedRows: PresenceRow[];
  noPunchIds: string[];
}

export function dayPosition(day: string, today: string = todayIsoParis()): DayPosition {
  return day === today ? 'today' : day < today ? 'past' : 'future';
}

/**
 * Minutes from the BUSINESS day's midnight (not the calendar day's).
 *
 * A shift belongs to the business day its clock-in fell in, and that day runs
 * to 04:00 the next morning (BUSINESS_DAY_CUTOFF_HOUR). So an instant whose
 * Paris calendar date is the day AFTER the business day sits past midnight on
 * this axis: a 00:43 clock-out on business day D is minute 1483, not 43. That
 * is what lets an 18:57 → 00:43 shift draw its true length instead of
 * wrapping backwards.
 */
export function minutesInBusinessDay(iso: string, day: string): number {
  const hm = timeToMinutes(parisHM(iso));
  return todayIsoParis(new Date(iso)) === day ? hm : hm + 24 * 60;
}

/**
 * Partition one business day's entries over a roster.
 *
 * `entries` may over-cover the day (callers fetch a superset window to catch
 * the 04:00 overhang — businessDayFetchRange); anything outside `day` is
 * dropped here. `employeeIds` is the roster to reckon absence against: only
 * these people can appear in `noPunchIds`, so the caller controls whether that
 * means "everyone" or "everyone under contract that day".
 */
export function computePresenceDay(
  // Structurally typed, like computeDayWorkedHours: the kiosk's own state
  // carries no entry id and no correction trace, and neither is read here.
  entries: PunchLike[],
  employeeIds: string[],
  day: string,
  today: string = todayIsoParis(),
): PresenceDay {
  const rows = new Map<string, PresenceRow>();

  for (const entry of entries) {
    if (businessDayOf(new Date(entry.clockIn)) !== day) continue;
    let row = rows.get(entry.employeeId);
    if (!row) {
      row = { employeeId: entry.employeeId, segments: [], open: false };
      rows.set(entry.employeeId, row);
    }
    const startMin = minutesInBusinessDay(entry.clockIn, day);
    const startLabel = parisHM(entry.clockIn);
    if (entry.clockOut === null) {
      row.segments.push({ startMin, endMin: null, label: `${startLabel}–…` });
      row.open = true;
    } else {
      const rawEnd = minutesInBusinessDay(entry.clockOut, day);
      row.segments.push({
        startMin,
        // A clock-out at or before the clock-in can only mean the next day.
        endMin: rawEnd <= startMin ? rawEnd + 24 * 60 : rawEnd,
        label: `${startLabel}–${parisHM(entry.clockOut)}`,
      });
    }
  }

  for (const row of rows.values()) row.segments.sort((a, b) => a.startMin - b.startMin);
  const byFirstPunch = (a: PresenceRow, b: PresenceRow): number =>
    (a.segments[0]?.startMin ?? 0) - (b.segments[0]?.startMin ?? 0);
  const all = [...rows.values()].sort(byFirstPunch);

  return {
    day,
    position: dayPosition(day, today),
    openRows: all.filter((r) => r.open),
    closedRows: all.filter((r) => !r.open),
    noPunchIds: employeeIds.filter((id) => !rows.has(id)),
  };
}

/** Section headings for a day — the meaning of each group shifts with the day (see the header note). */
export function presenceSectionLabels(position: DayPosition): {
  open: string;
  closed: string;
  none: string;
} {
  return position === 'today'
    ? { open: 'Présent·e·s', closed: 'Parti·e·s', none: 'Pas de pointage' }
    : { open: 'Sans départ', closed: 'Présent·e·s', none: 'En repos' };
}
